import { useEffect, useRef } from 'react';
import { Pressable, Platform as RNPlatform, StyleSheet, Text, TextInput, View } from 'react-native';
import { PIN_LENGTH } from 'utils/constants';
import { calcSizeForDevice, getResponsiveSize } from 'utils/index';
import { Platform } from 'utils/platform';

type ProfilePinFieldProps = {
  length?: number;
  value: string;
  error?: boolean;
  onChange: (value: string) => void;
};

const isWebMobile = RNPlatform.OS === 'web' && Platform.isMobileWeb();

export const ProfilePinField = ({
  length = PIN_LENGTH,
  value,
  error,
  onChange,
}: ProfilePinFieldProps) => {
  const inputRef = useRef<TextInput>(null);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        isWebMobile &&
        (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement)
      ) {
        return;
      }
      if (/^[0-9]$/.test(e.key)) {
        e.preventDefault();
        if (value.length < length) {
          onChange(value + e.key);
        }
      }
      if (!Platform.isTV && e.key === 'Backspace') {
        e.preventDefault();
        onChange(value.slice(0, -1));
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [value, length, onChange]);

  const handleChangeText = (text: string) => {
    const next = text.replace(/\D/g, '').slice(0, length);
    if (next !== value) onChange(next);
  };

  const activeIndex = value.length >= length ? length - 1 : value.length;

  const boxes = (
    <View style={[styles.rowContainer, !Platform.isTV && styles.webRowContainer]}>
      {Array.from({ length }).map((_, index) => {
        const isFilled = index < value.length;
        const isCurrent = index === activeIndex;

        return (
          <View
            key={index}
            style={[
              styles.box,
              Platform.isTV ? styles.tvBox : styles.webBox,
              isCurrent && styles.activeBox,
              error && styles.errorBox,
            ]}>
            <Text style={styles.text}>{isFilled ? '•' : ''}</Text>
          </View>
        );
      })}
    </View>
  );

  return (
    <View style={styles.wrapper}>
      {isWebMobile ? (
        <Pressable
          style={styles.webInputTouchable}
          onPress={() => inputRef.current?.focus()}
          accessibilityRole="button"
          accessibilityLabel="Campo de PIN">
          <TextInput
            ref={inputRef}
            style={styles.hiddenInput}
            value={value}
            onChangeText={handleChangeText}
            maxLength={length}
            keyboardType="number-pad"
            caretHidden
            contextMenuHidden
            inputMode="numeric"
          />
          {boxes}
        </Pressable>
      ) : (
        boxes
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  wrapper: {
    alignSelf: 'flex-start',
  },
  webInputTouchable: {
    position: 'relative',
  },
  hiddenInput: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    opacity: 0,
    minWidth: 1,
    minHeight: 1,
    padding: 0,
    margin: 0,
    borderWidth: 0,
    shadowColor: 'transparent',
    shadowOpacity: 0,
    shadowRadius: 0,
    shadowOffset: { width: 0, height: 0 },
  },
  rowContainer: {
    flexDirection: 'row',
  },
  webRowContainer: {
    gap: 14,
  },
  box: {
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#001133',
    borderWidth: 2,
    borderColor: '#1E40AF',
    borderRadius: 6,
  },
  tvBox: {
    width: calcSizeForDevice(124),
    height: calcSizeForDevice(95),
    marginRight: calcSizeForDevice(14),
  },
  webBox: {
    width: Number(getResponsiveSize({ base: 56, md: 124 })),
    height: Number(getResponsiveSize({ base: 48, md: 95 })),
  },
  activeBox: {
    borderColor: '#FFF',
    shadowColor: '#FFF',
    shadowOpacity: 0.8,
    shadowRadius: 8,
  },
  errorBox: {
    borderColor: '#FF4D4F',
    shadowColor: '#FF4D4F',
  },
  text: {
    fontSize: calcSizeForDevice(32),
    fontWeight: '600',
    color: '#FFF',
  },
});
